import { Component } from '@theme/component';

/**
 * Client-side group filter for the shop-v2 collection page.
 *
 * Pills behave as a single-select radiogroup: selecting one shows only the
 * matching product group; "all" shows every group. No reload, no server round-trip.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} pills
 *
 * @extends {Component<Refs>}
 */
class ShopFilter extends Component {
  requiredRefs = ['pills'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
  }

  /**
   * The product-group sections this filter toggles. They live next to the pills
   * (not inside this element), so we look them up from the section root.
   *
   * @returns {HTMLElement[]}
   */
  get groups() {
    const root = this.closest('.shop-collection');
    return root ? Array.from(root.querySelectorAll('.shop-collection__group')) : [];
  }

  /**
   * Click handler (delegated from the radiogroup container).
   *
   * @param {Event} event
   */
  select(event) {
    const pill = event.target instanceof Element ? event.target.closest('[data-group]') : null;
    if (pill instanceof HTMLElement) this.#activate(pill, false);
  }

  /**
   * @param {HTMLElement} pill - The pill to activate.
   * @param {boolean} focus - Whether to move focus to it (keyboard nav).
   */
  #activate(pill, focus) {
    const group = pill.dataset.group;

    for (const candidate of this.refs.pills) {
      const active = candidate === pill;
      candidate.setAttribute('aria-checked', String(active));
      candidate.tabIndex = active ? 0 : -1;
    }

    if (focus) pill.focus();

    const updateGroups = () => {
      for (const section of this.groups) {
        section.hidden = group !== 'all' && section.dataset.groupId !== group;
      }
    };

    // Progressive enhancement: smooth cross-fade + size morph where supported.
    // The theme sets `:root { view-transition-name: none }`, which disables the
    // default root transition — so we name the groups container ourselves for the
    // duration of the transition (and clear it afterwards so it never interferes
    // with the theme's navigation transitions).
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const groupsEl = this.closest('.shop-collection')?.querySelector('.shop-collection__groups');

    if (typeof document.startViewTransition === 'function' && !reduceMotion && groupsEl instanceof HTMLElement) {
      groupsEl.style.viewTransitionName = 'shop-collection-groups';
      const transition = document.startViewTransition(updateGroups);
      transition.finished.finally(() => {
        groupsEl.style.viewTransitionName = '';
      });
    } else {
      updateGroups();
    }
  }

  /**
   * Arrow / Home / End keyboard navigation for the radiogroup.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const { pills } = this.refs;
    const current = pills.findIndex((pill) => pill.getAttribute('aria-checked') === 'true');
    let next = -1;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % pills.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + pills.length) % pills.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = pills.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(pills[next], true);
  };
}

if (!customElements.get('shop-filter')) {
  customElements.define('shop-filter', ShopFilter);
}
